import findLast from 'lodash/findLast';
import { DataModel, PresentationEventTypes, OperationType } from '@tencent/slide-sdk-model';
import type { IEventManager } from '@tencent/slide-sdk-model/src/manager/event-manager';
import type { IOperationRecord, IOperationRecordMap, IPresentationDataModel } from '@tencent/slide-sdk-model';

type IRecordMap = {
    [OperationType.API]: Omit<IOperationRecordMap[OperationType.API], 'args'> & { args: string };
    [OperationType.Undo]: IOperationRecordMap[OperationType.Undo];
    [OperationType.Redo]: IOperationRecordMap[OperationType.Redo];
    [OperationType.BeginTransaction]: IOperationRecordMap[OperationType.BeginTransaction];
    [OperationType.EndTransaction]: IOperationRecordMap[OperationType.EndTransaction];
    [OperationType.EnqueueMutations]: IOperationRecordMap[OperationType.EnqueueMutations];
};

export class OperationLogger {
    private presentation: IPresentationDataModel;
    private events: IEventManager;
    private records: IRecordMap[OperationType][] = [];
    private firstSnapshot?: string;
    private lastSnapshot?: string;
    private idSeed?: number;
    private runningCount = 0;

    public init(presentation: IPresentationDataModel) {
        this.presentation = presentation;
        this.events = this.presentation.getEvents();
        if (!this.idSeed) {
            this.idSeed = this.presentation.getIDGenerator().seed?.();
        }
        this.events.on(PresentationEventTypes.OperationBegin, (type, operationInfo) => {
            try {
                if (!this.firstSnapshot) {
                    this.firstSnapshot = JSON.stringify(this.presentation.getOwnedData());
                }
                if (operationInfo.type === OperationType.EnqueueMutations) {
                    this.records.push(operationInfo);
                } else if (this.runningCount < 1) {
                    if (operationInfo.type === OperationType.API) {
                        this.records.push({ ...operationInfo, args: this.stringifyOperationArgs(operationInfo.args) });
                    } else {
                        this.records.push(operationInfo);
                    }
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
            }
            this.runningCount += 1;
        });
        this.events.on(PresentationEventTypes.OperationEnd, (type, endAt) => {
            try {
                // 由于目前接口是同步的，所以直接更新最后一个 record 即可
                if (this.runningCount <= 1) {
                    const record = findLast(
                        this.records,
                        (record) =>
                            record.type !== OperationType.EndTransaction &&
                            record.type !== OperationType.BeginTransaction &&
                            record.type !== OperationType.EnqueueMutations,
                    );
                    if (record) {
                        (record as IRecordMap[OperationType]).endAt = endAt;
                    }
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
            }
            this.runningCount -= 1;
        });
        this.events.on(PresentationEventTypes.OperationError, () => {
            this.runningCount -= 1;
        });
        this.events.on(PresentationEventTypes.BeginTransaction, (depth) => {
            if (depth === 0 && this.runningCount < 1) {
                this.records.push({ type: OperationType.BeginTransaction, beginAt: performance.now() });
            }
        });
        this.events.on(PresentationEventTypes.EndTransaction, (depth) => {
            if (depth === 0 && this.runningCount < 1) {
                this.records.push({ type: OperationType.EndTransaction, endAt: performance.now() });
            }
        });
    }

    /**
     * 导出 json
     */
    public export(): string {
        this.lastSnapshot = JSON.stringify(this.presentation.getOwnedData());
        if (!this.firstSnapshot) {
            this.firstSnapshot = JSON.stringify(this.presentation.getOwnedData());
        }
        const { firstSnapshot, lastSnapshot, idSeed, records } = this;
        return JSON.stringify({
            records: this.getParsedRecords(records),
            idSeed,
            firstSnapshot: JSON.parse(firstSnapshot ?? ''),
            lastSnapshot: JSON.parse(lastSnapshot ?? ''),
        });
    }

    public getRecords(start?: number, filterType?: OperationType) {
        const tmpRecords = this.records.slice(start ?? 0);
        if (!filterType) {
            return this.getParsedRecords(tmpRecords);
        }
        return this.getParsedRecords(tmpRecords.filter((record) => record.type === filterType));
    }

    /**
     * 导出并下载，浏览器中使用
     */
    public exportAndDownload(): void {
        try {
            const json = this.export();
            const blob = new Blob([json], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'slide-operations.logs.json';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    }

    /**
     * JSON 序列化特殊处理： DataModel 只保留 data
     * @param operationArgs 待序列化参数
     * @returns 序列化后的字符串
     */
    private stringifyOperationArgs(operationArgs: IOperationRecordMap[OperationType.API]['args']) {
        try {
            return JSON.stringify(operationArgs, (key: string, value: unknown) => {
                if (value instanceof DataModel) {
                    return { id: value.store.data.id, type: value.store.data.type, isDataModel: true };
                }
                return value;
            });
        } catch (e) {
            return `args stringify error: ${e}`;
        }
    }

    private getParsedRecords(records: IRecordMap[OperationType][]): IOperationRecord[] {
        return records.map((record) => {
            if (record.type === OperationType.API) {
                return { ...record, args: JSON.parse(record.args) };
            }
            return record;
        });
    }
}

// 逻辑来源 https://github.com/moll/json-stringify-safe/blob/master/stringify.js
// 解决 JSON 循环引用问题：循环引用的地方用字符串替代
export function serializer() {
    const stack: unknown[] = [];
    const keys: string[] = [];
    const cycleReplacer = function (key: string, value: unknown) {
        if (stack[0] === value) return '[Circular ~]';
        return `[Circular ~.${keys.slice(0, stack.indexOf(value)).join('.')}]`;
    };

    return function (this: unknown, key: string, value: unknown) {
        if (stack.length <= 0) {
            stack.push(value);
            return value;
        }
        const thisPos = stack.indexOf(this);
        // ~thisPos 逻辑上等价于 thisPos > 0, 因为(~-1 = 0)
        ~thisPos ? stack.splice(thisPos + 1) : stack.push(this);
        ~thisPos ? keys.splice(thisPos, Infinity, key) : keys.push(key);
        if (~stack.indexOf(value)) {
            return cycleReplacer.call(this, key, value);
        }
        return value;
    };
}
